/* eslint-disable react/jsx-no-bind */
import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import get from "lodash/get";
import compact from "lodash/compact";
import qs from "qs";
import verifyRecaptcha from "src/common/facades/google/recaptcha";
import { logSaveForLater } from "../../../../facades/eagle/log";
import { newsletter as subscribe } from "../../../../facades/eagle/subscriptions";
import { sendSaveForLaterEmail } from "../../../../facades/website";
import {
  useErrorMessages,
  useGlobalContent,
  useQueryParams,
  useModel,
  useCountry,
} from "../../../hooks";
import CloseIcon from "wonderbly-components/lib/CloseIcon";
import Form from "wonderbly-components/lib/Form";

const getSignup = (props, disableSignup) => {
  if (disableSignup) {
    return null;
  }

  const field = {
    name: "optIn",
    type: props.type,
    value: props.value,
    label: props.accept,
  };

  if (field.type === "radio") {
    delete field.label;
    field.options = [
      {
        value: true,
        label: props.accept,
      },
      {
        value: false,
        label: props.decline,
      },
    ];
  }

  return field;
};

const SaveForLaterForm = (props) => {
  const location = useLocation();
  const { topology } = useModel();
  const { marketingOptIn } = useGlobalContent("general");
  const { params } = useQueryParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { getErrorMessage } = useErrorMessages();
  const { country } = useCountry();

  const onHandleSubmit = async (values, { setError }) => {
    if (topology.google.siteKey) {
      const token = await executeRecaptcha("save_for_later");

      // validate recaptcha
      try {
        await verifyRecaptcha(token);
      } catch ({ details }) {
        const errors = details.errors;
        const errorCode = errors[0].code;
        setError("base", {
          type: errorCode,
          message: getErrorMessage((error) => get(error, errorCode)),
        });
        return;
      }
    }

    // submit form
    try {
      await sendSaveForLaterEmail(
        values.email,
        location.pathname + location.search,
        props.productName
      );

      if (values.optIn) {
        await subscribe(
          {
            email: values.email,
            signup_source: "site:saveforlater",
          },
          country
        );

        // wait for user to be subscribed so they exist in db.
        // sent this off async - this shouldn't be blocking
        logSaveForLater({
          email: values.email,
          slug: props.productId,
          save_for_later_url: location.pathname + location.search,
          book_recipient_name: qs.parse(location.search, {
            ignoreQueryPrefix: true,
          }).name,
        });
      }

      props.onHandleSubmitCallback();
    } catch (errors) {
      const err = { type: "base", ...errors[0] };
      setError("base", err);
    }
  };

  return (
    <div className="SaveForLaterModal__inner">
      <div className="SaveForLaterModal__header">
        <CloseIcon onHandleClick={props.onHandleClose} color="#00294C" />
      </div>
      <h4 className="SaveForLaterModal__title">{props.title}</h4>
      <p>{props.description}</p>
      <Form
        fields={compact([
          {
            type: "email",
            name: "email",
            placeholder: props.placeholder,
            required: true,
          },
          getSignup(marketingOptIn, !props.showOptIn || params.uiDisableSignup),
          {
            type: "submit",
            label: props.ctaText,
          },
        ])}
        onSubmit={onHandleSubmit}
      />
    </div>
  );
};

SaveForLaterForm.propTypes = {
  productName: PropTypes.string,
  productId: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  placeholder: PropTypes.string,
  ctaText: PropTypes.string,
  showOptIn: PropTypes.bool,
  onHandleClose: PropTypes.func,
  onHandleSubmitCallback: PropTypes.func.isRequired,
};

export default SaveForLaterForm;
